/**
 * IntakeForm — formulaire public intake (Chunk 9.N.2 v2 — Apple-like simplicity).
 *
 * Hiérarchie unique top → bottom (1 colonne max-w-xl, branding-aligned) :
 *   1. Title block hors-card — H1 Inter 30/34 semibold + sous-titre projet
 *   2. FileDropzone (HERO — la zone visuellement dominante ; composant
 *      générique factor depuis l'ex-`PublicFileDropzone` lors du chunk 9.T.3,
 *      consommé ici via i18nNamespace `"public_intake.form.files"` default)
 *   3. Tip ⓘ NAS (slate-600 sur fond slate-50/60, neutre, NON-anxiogène)
 *   4. <details> fermé "Ajouter un message ou un courriel (optionnel)"
 *      → progressive disclosure pour les 80% d'utilisateurs qui n'en ont pas besoin
 *   5. Honeypot `website` (off-screen, cadrage F2 §3.4)
 *   6. ConsentCheckbox Loi 25 (pré-décoché, obligatoire)
 *   7. Toast inline (si erreur récupérable post-submit)
 *   8. CTA Submit "Envoyer" full-width, primary brand-600
 *
 * Backend dispatch via `useSubmitPublicIntake` mutation result :
 *   - `received` → `onSuccess(intakeEventId)` → parent rend SuccessState
 *   - `invalid`  → `onTokenInvalid()` → parent rend InvalidState (anti-leak)
 *   - `payload` / `file_rejected` / `rate_limited` / `internal` →
 *     toast inline, form reste affiché pour retry
 *
 * Anti-leak strict :
 *   - Pas de console.log avec details techniques.
 *   - Pas de mention de la cause exacte côté UI ("token expired" etc.).
 *   - Sur `kind:"invalid"`, on bascule en InvalidState (parité GET fail).
 *
 * WIG/UX appliqués :
 *   - beforeunload guard (unsaved files/comment + mutation pas success)
 *   - email validate on blur (pas keystroke) — moins d'erreurs prématurées
 *   - submit auto-focus first invalid field (consent ou dropzone)
 *   - aria-describedby comment counter
 *   - submit spinner motion-safe + motion-reduce:animate-none fallback
 */

import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FileDropzone } from "../../../components/FileDropzone";
import {
  useSubmitPublicIntake,
  type IntakeSubmissionResult,
} from "../../../lib/queries/public-intake";

import { ConsentCheckbox } from "./ConsentCheckbox";

const COMMENT_MAX = 500;
// Validation email RFC simplifiée (parité Zod côté backend).

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type IntakeFormProps = {
  readonly token: string;
  readonly projectTitle: string;
  readonly onSuccess: (intakeEventId: string) => void;
  readonly onTokenInvalid: () => void;
};

type SubmitToast =
  | { readonly kind: "payload" }
  | { readonly kind: "file_rejected" }
  | { readonly kind: "rate_limited"; readonly retryAfterSec: number | null }
  | { readonly kind: "internal" }
  | null;

export function IntakeForm({
  token,
  projectTitle,
  onSuccess,
  onTokenInvalid,
}: IntakeFormProps): JSX.Element {
  const { t } = useTranslation();
  const commentId = useId();
  const commentCounterId = `${commentId}-count`;
  const emailId = useId();
  const emailErrorId = `${emailId}-error`;
  const honeypotId = useId();

  const [files, setFiles] = useState<readonly File[]>([]);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [toast, setToast] = useState<SubmitToast>(null);

  const consentRef = useRef<HTMLDivElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const mutation = useSubmitPublicIntake(token);

  // WIG — Forms : warn before navigation with unsaved changes.
  // S'applique uniquement quand l'utilisateur a déjà mis du travail (fichiers
  // ou commentaire) et que la mutation n'a pas encore réussi.
  useEffect(() => {
    const hasUnsavedWork = (files.length > 0 || comment.trim().length > 0) && !mutation.isSuccess;
    if (!hasUnsavedWork) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Modern browsers ignore custom messages for security ; just trigger
      // the native confirmation dialog.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [files.length, comment, mutation.isSuccess]);

  // Validation email : on commence à valider seulement après blur (WIG forms),
  // sauf si l'utilisateur a déjà submit (alors on montre l'erreur live aussi).
  const emailValid = email === "" || EMAIL_RE.test(email);
  const emailShowError = !emailValid && (emailTouched || submitAttempted);
  const consentMissing = !consent;
  const filesMissing = files.length === 0;
  const isSubmitting = mutation.isPending;

  const canSubmit = !filesMissing && !consentMissing && emailValid && !isSubmitting;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setSubmitAttempted(true);
    setToast(null);

    if (!canSubmit) {
      // WIG forms : focus first invalid field on submit.
      // Priorité : files (dropzone) > consent.
      if (filesMissing) {
        dropzoneRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
      } else if (consentMissing) {
        consentRef.current?.querySelector<HTMLInputElement>("input[type='checkbox']")?.focus();
      }
      return;
    }

    const formData = new FormData();
    formData.append(
      "consent",
      JSON.stringify({
        accepted: true,
        timestamp: new Date().toISOString(),
      }),
    );
    if (comment.trim()) formData.append("comment", comment.trim());
    if (email.trim()) formData.append("email", email.trim());
    // Honeypot — submit même vide pour que le backend voie que le champ existe
    // côté FE (legitimate user laisse vide → backend autorise ; bot remplit →
    // backend détecte). Pas d'`appendIfNotEmpty` ici.
    formData.append("website", honeypot);
    for (const file of files) {
      formData.append("files", file, file.name);
    }

    mutation.mutate(formData, {
      onSuccess: (result: IntakeSubmissionResult) => {
        if (result.kind === "received") {
          onSuccess(result.intakeEventId);
          return;
        }
        if (result.kind === "invalid") {
          onTokenInvalid();
          return;
        }
        if (result.kind === "rate_limited") {
          setToast({ kind: "rate_limited", retryAfterSec: result.retryAfterSec });
          return;
        }
        // payload / file_rejected / internal
        setToast({ kind: result.kind });
      },
    });
  };

  const toastMessage: string | null = (() => {
    if (!toast) return null;
    if (toast.kind === "payload") return t("public_intake.errors.submit_payload");
    if (toast.kind === "file_rejected") return t("public_intake.errors.submit_files");
    if (toast.kind === "rate_limited") {
      return toast.retryAfterSec
        ? t("public_intake.errors.submit_rate_limit", { seconds: toast.retryAfterSec })
        : t("public_intake.errors.submit_rate_limit_no_seconds");
    }
    if (toast.kind === "internal") return t("public_intake.errors.submit_internal");
    return null;
  })();

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-pretty text-3xl font-semibold tracking-tight text-slate-900 sm:text-[34px]">
          {t("public_intake.page.title")}
        </h1>
        <p className="text-sm text-slate-600">
          {t("public_intake.page.subtitle_with_project", { projectTitle })}
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        noValidate
        className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-12px_rgba(15,23,42,0.08)] sm:p-7"
      >
        {/* Dropzone hero — l'action principale visuellement dominante */}
        <div ref={dropzoneRef}>
          <FileDropzone
            files={files}
            onFilesChange={setFiles}
            disabled={isSubmitting}
            errorMessage={
              submitAttempted && filesMissing
                ? t("public_intake.form.files.errors.empty")
                : undefined
            }
          />
        </div>

        {/* Tip NAS — info neutre slate-600, pas amber pill */}
        <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5 text-xs text-slate-600">
          <svg
            viewBox="0 0 16 16"
            fill="none"
            className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.25" />
            <path d="M8 7.25v3.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" />
            <circle cx="8" cy="5.25" r="0.6" fill="currentColor" />
          </svg>
          <p className="leading-relaxed">{t("public_intake.form.warning_nas")}</p>
        </div>

        {/* Optional fields disclosure — fermé par défaut (Apple-like) */}
        <details className="group rounded-lg border border-slate-200 bg-white">
          <summary className="focus-visible:ring-brand-500 flex cursor-pointer list-none items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 [&::-webkit-details-marker]:hidden">
            <span>{t("public_intake.form.optional_disclosure")}</span>
            <svg
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              className="h-4 w-4 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-180 motion-reduce:transition-none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 6l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </summary>
          <div className="space-y-4 border-t border-slate-100 px-3 pb-4 pt-3">
            {/* Comment */}
            <div className="space-y-1">
              <label htmlFor={commentId} className="block text-sm font-medium text-slate-800">
                {t("public_intake.form.comment.label")}
              </label>
              <textarea
                id={commentId}
                name="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
                placeholder={t("public_intake.form.comment.placeholder")}
                rows={3}
                maxLength={COMMENT_MAX}
                disabled={isSubmitting}
                aria-describedby={commentCounterId}
                className="focus:border-brand-500 focus:ring-brand-500 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-1 motion-reduce:transition-none"
              />
              <p id={commentCounterId} className="text-right text-xs tabular-nums text-slate-500">
                {t("public_intake.form.comment.char_count", { count: comment.length })}
              </p>
            </div>

            {/* Email */}
            <div className="space-y-1">
              <label htmlFor={emailId} className="block text-sm font-medium text-slate-800">
                {t("public_intake.form.email.label")}
              </label>
              <input
                id={emailId}
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                placeholder={t("public_intake.form.email.placeholder")}
                autoComplete="email"
                inputMode="email"
                spellCheck={false}
                disabled={isSubmitting}
                aria-invalid={emailShowError ? "true" : undefined}
                aria-describedby={emailShowError ? emailErrorId : undefined}
                className="focus:border-brand-500 focus:ring-brand-500 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm transition-colors placeholder:text-slate-400 focus:outline-none focus:ring-1 aria-[invalid=true]:border-red-400 aria-[invalid=true]:focus:ring-red-400 motion-reduce:transition-none"
              />
              {emailShowError && (
                <p id={emailErrorId} role="alert" className="text-xs font-medium text-red-700">
                  {t("public_intake.form.email.invalid")}
                </p>
              )}
            </div>
          </div>
        </details>

        {/* Honeypot — invisible aux humains et SR (off-screen + aria-hidden) */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "-9999px",
            width: 1,
            height: 1,
            overflow: "hidden",
          }}
        >
          <label htmlFor={honeypotId}>Website (do not fill)</label>
          <input
            id={honeypotId}
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
          />
        </div>

        {/* Consent */}
        <div ref={consentRef}>
          <ConsentCheckbox
            checked={consent}
            onChange={setConsent}
            disabled={isSubmitting}
            showError={submitAttempted && consentMissing}
          />
        </div>

        {/* Toast inline — récupérable, dismissible implicite (disparaît au prochain submit) */}
        {toastMessage && (
          <p
            role="alert"
            aria-live="polite"
            className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700"
          >
            {toastMessage}
          </p>
        )}

        {/* Submit — primary CTA */}
        <button
          type="submit"
          disabled={!canSubmit}
          className="bg-brand-600 hover:bg-brand-700 active:bg-brand-800 focus-visible:ring-brand-500 inline-flex w-full touch-manipulation items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none motion-reduce:transition-none"
        >
          {isSubmitting && (
            <span
              aria-hidden="true"
              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white motion-reduce:animate-none"
            />
          )}
          {isSubmitting ? t("public_intake.form.submitting") : t("public_intake.form.submit")}
        </button>
      </form>
    </div>
  );
}
